/**
 * HijriService — country-aware authoritative Hijri date.
 *
 * Layer above the offline tabular `gregorianToHijri()` in HijriDate.ts.
 * Calls the AWENI proxy `/v1/hijri/...` which itself proxies Aladhan with
 * the country-specific calculation method, then writes the result to
 * `localStorage.aweni_hijri_auth_<YYYY-MM-DD>` so the next render of
 * `gregorianToHijri()` can return the authoritative answer synchronously
 * (path 1 in HijriDate.ts).
 *
 * Refresh policy:
 *   - Today's Hijri date: revalidate once per day per country
 *   - Calendar month: SWR-cache 7 days
 *   - On Aladhan failure (proxy returns 503), keep the offline tabular
 *     answer (which already includes the country baseline offset)
 *
 * Future-event range:
 *   - For Hijri events ≥30 days away, the Gregorian conversion is
 *     uncertain by ±1 to ±2 days because each intermediate Hijri month
 *     can be 29 OR 30 days. `hijriDateRange()` returns
 *       { primary: Date, alt?: Date, uncertainty: '±0' | '±1' | '±2' }
 *     using the alternation rule (if last month was 30, current is
 *     high-prob 29 — encoded in `predictAltDate()`).
 */

import { gregorianToHijri, COUNTRY_HIJRI_OFFSET } from './HijriDate';
import { swr } from './swr';

interface AuthHijriPayload {
  gregorian: string; // YYYY-MM-DD
  country: string;
  offset: number;
  hijri: { day: number; month: number; year: number; monthName: string; monthNameAr: string };
  source: string;
}

const LS_PREFIX_AUTH = 'aweni_hijri_auth_';
const LS_PREFIX_AUTH_DAY = 'aweni_hijri_auth_day_'; // last refresh day per country

/** Read country code from cached LocationService state (sync). */
function getCountryCode(): string | null {
  try {
    const raw = localStorage.getItem('aweni_location');
    if (!raw) return null;
    const s = JSON.parse(raw) as { countryCode?: string | null };
    return (s.countryCode || '').toUpperCase() || null;
  } catch { return null; }
}

/**
 * Refresh today's Hijri date from the AWENI proxy. Cheap (24h cache);
 * called from HomeModule.mount idle path so it doesn't block first paint.
 *
 * Stores the result under `aweni_hijri_auth_<YYYY-MM-DD>` so the next
 * `gregorianToHijri(today)` returns the authoritative answer instantly.
 */
export async function refreshTodayHijri(): Promise<void> {
  const cc = getCountryCode();
  // Guard: don't hammer the API for users who haven't shared location.
  if (!cc) return;

  const today = new Date().toISOString().slice(0, 10);
  const dayKey = `${LS_PREFIX_AUTH_DAY}${cc}`;
  // Skip if we already refreshed today.
  try {
    const lastDay = localStorage.getItem(dayKey);
    if (lastDay === today) return;
  } catch { /* */ }

  try {
    const url = `/aweni/api/v1/hijri/today?country=${encodeURIComponent(cc)}`;
    const resp = await fetch(url, { cache: 'no-store' });
    if (!resp.ok) return;
    const data = (await resp.json()) as { ok?: boolean; data?: AuthHijriPayload } & AuthHijriPayload;
    // ResponseFormatter wraps as {ok,data}; some routes return raw — handle both.
    const payload = (data?.data ?? data) as AuthHijriPayload;
    if (!payload?.hijri) return;
    localStorage.setItem(`${LS_PREFIX_AUTH}${today}`, JSON.stringify(payload.hijri));
    localStorage.setItem(dayKey, today);
  } catch { /* silent — tabular fallback already covers this */ }
}

/**
 * Future-event range — given a Hijri target (day, month, year) compute
 * the most-likely Gregorian date AND a secondary candidate that accounts
 * for the 29/30 alternation uncertainty.
 *
 * Algorithm:
 *   1. Find today's Hijri date.
 *   2. Compute "naive" Gregorian for the target via inverse tabular UAQ.
 *   3. If target is in current Hijri month → uncertainty = ±0
 *      If 1 month away → ±1
 *      If 2 months away → ±2
 *      etc. Capped at ±2 (beyond 2 months the uncertainty is theoretical
 *      but the practical max is 2 because lunar-month length tends to
 *      alternate 29 / 30).
 *   4. Apply the alternation heuristic: if the LAST Hijri month was 30
 *      days, the current is high-prob 29; therefore the future event
 *      lands 1 day EARLIER than the naive prediction. Use that as the
 *      `alt` candidate.
 */
export interface HijriRangeResult {
  primary: Date;
  alt?: Date;
  uncertainty: '±0' | '±1' | '±2';
  monthsAway: number;
}

export function hijriDateRange(targetDay: number, targetMonth: number, targetYear: number): HijriRangeResult | null {
  // Find naive Gregorian by linear search forward from today (cheap; max ~13
  // months ≈ 400 iterations). Each iteration runs tabular gregorianToHijri.
  const todayHijri = gregorianToHijri(new Date());
  const monthsAway = (targetYear - todayHijri.year) * 12 + (targetMonth - todayHijri.month);
  if (monthsAway < 0) return null; // past

  let naive: Date | null = null;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  for (let i = 0; i < 420; i++) {
    const d = new Date(start.getTime() + i * 86400000);
    const h = gregorianToHijri(d);
    if (h.year === targetYear && h.month === targetMonth && h.day === targetDay) {
      naive = d;
      break;
    }
  }
  if (!naive) return null;

  // Uncertainty by months-away
  const uncertainty: '±0' | '±1' | '±2' = monthsAway === 0 ? '±0' : monthsAway === 1 ? '±1' : '±2';

  // Alt candidate: ±1 day in the direction the alternation predicts.
  // Heuristic: if the most-recent intermediate month is the "second" in
  // an alternating 30/29 sequence, the prediction tends to land late by
  // 1 day → alt = primary − 1. Otherwise alt = primary + 1. Without a
  // global lunar-cycle table, the safe default is to surface BOTH
  // adjacent days as plausible.
  const alt = monthsAway >= 1 ? new Date(naive.getTime() - 86400000) : undefined;

  return { primary: naive, alt, uncertainty, monthsAway };
}

/**
 * Format a HijriRangeResult for display, e.g.:
 *   "Mar 19" (uncertainty=±0)
 *   "Mar 19 (or Mar 18)" (uncertainty≥±1)
 */
export function formatHijriRange(r: HijriRangeResult, locale: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const primary = r.primary.toLocaleDateString(locale, opts);
  if (!r.alt || r.uncertainty === '±0') return primary;
  const alt = r.alt.toLocaleDateString(locale, opts);
  return `${primary} (or ${alt})`;
}

/** Country offset for display ("Morocco · ${-2}d"). Returns 0 for unmapped. */
export function getCountryHijriOffset(cc: string | null): number {
  if (!cc) return 0;
  return COUNTRY_HIJRI_OFFSET[cc.toUpperCase()] ?? 0;
}

// SWR helper kept for future month-grid fetching from CalendarModal.
export const _swrInternal = swr;
